import React, {useEffect} from 'react'
import {cx} from '@emotion/css'
import {useAnimation} from 'motion/react'
import {clamp} from 'ramda'

import {usePreviousValue} from '@vio/core/src/utils/react/hooks/usePreviousValue'

import {DOT_SECTION_SIZE, DOT_WIDTH} from './constants'
import {DotIndicator, IndicatorWrapper} from './styled'

interface Props {
  amount: number
  hasDarkGradient: boolean
  currentIndex: number
  width: number
  isRtl: boolean
  invertDotColor?: boolean
}

const calculateDotPosition = (i: number, currentIndex: number, width: number) =>
  width / 2 -
  currentIndex * DOT_SECTION_SIZE +
  i * DOT_SECTION_SIZE -
  DOT_WIDTH / 2

const getIndicatorStyle = ({
  indicatorIndex,
  currentIndex,
  width,
  isRtl
}: {
  indicatorIndex: number
  currentIndex: number
  width: number
  isRtl: boolean
}) => ({
  x:
    (isRtl ? -1 : 1) *
    calculateDotPosition(indicatorIndex, currentIndex, width),
  scale: 1 / clamp(1, 7, Math.abs(indicatorIndex - currentIndex)),
  display: Math.abs(indicatorIndex - currentIndex) > 3 ? 'none' : 'block',
  opacity: indicatorIndex === currentIndex ? 1 : 0.5
})

export const CurrentPaneIndicators = ({
  currentIndex,
  hasDarkGradient = true,
  amount,
  width,
  isRtl,
  invertDotColor = false
}: Props) => {
  const previousIndex = usePreviousValue(currentIndex)
  const controls = useAnimation()

  useEffect(() => {
    const immediate = Math.abs((previousIndex ?? 0) - currentIndex) > 1 // No animation when we roll over to/from 0
    controls.start((indicatorIndex: number) => ({
      ...getIndicatorStyle({indicatorIndex, currentIndex, width, isRtl}),
      ...(immediate
        ? {
            transition: {
              duration: 0
            }
          }
        : {
            transition: {
              type: 'spring',
              bounce: 0
            }
          })
    }))
  }, [currentIndex, width, controls, previousIndex])

  return (
    <IndicatorWrapper className={cx(hasDarkGradient && '--has-dark-gradient')}>
      {[...new Array(amount)].map((_, indicatorIndex) => (
        <DotIndicator
          className={cx(
            isRtl ? '--is-rtl' : '--is-ltr',
            invertDotColor ? '--inverse-color' : ''
          )}
          key={indicatorIndex} // eslint-disable-line react/no-array-index-key
          custom={indicatorIndex}
          initial={getIndicatorStyle({
            indicatorIndex,
            currentIndex,
            width,
            isRtl
          })}
          animate={controls}
        />
      ))}
    </IndicatorWrapper>
  )
}
